import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Send, RefreshCw, ArrowLeft, CheckCircle2, AlertCircle } from 'lucide-react';
import Button from './ui/Button';
import Card from './ui/Card';

const VerificationPending = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendStatus, setResendStatus] = useState(''); // 'success', 'error', ''
  
  const email = location.state?.email || '';
  const message = location.state?.message || 'Please check your email to verify your account.';
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleResendEmail = async () => {
    if (!email) {
      setResendMessage('Email address not found. Please register again.');
      setResendStatus('error');
      return;
    }

    try {
      setResending(true);
      setResendMessage('');
      setResendStatus('');
      
      const response = await axios.post(`${API_BASE_URL}/api/auth/resend-verification`, {
        email: email
      });

      setResendMessage(response.data.message);
      setResendStatus('success');
      
    } catch (error) {
      console.error('Resend verification error:', error);
      setResendMessage(error.response?.data?.detail || 'Failed to resend verification email. Please try again.');
      setResendStatus('error');
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/');
  };

  const handleBackToRegister = () => {
    navigate('/register');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--gradient-brand)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <Card style={{ 
        maxWidth: '500px', 
        width: '100%',
        textAlign: 'center'
      }}>
        <Card.Content style={{ padding: '48px 32px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            margin: '0 auto 24px',
            background: 'var(--gradient-info)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Mail size={40} color="white" />
          </div>
          
          <h1 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: 'var(--color-text-primary)',
            margin: '0 0 12px 0'
          }}>
            Check Your Email
          </h1>
          
          <p style={{
            color: 'var(--color-text-muted)',
            fontSize: '16px',
            margin: '0 0 24px 0',
            lineHeight: '1.5'
          }}>
            {message}
          </p>
          
          {email && (
            <div style={{
              background: '#f0f9ff',
              border: '1px solid var(--color-info-border)',
              borderRadius: '8px',
              padding: '16px',
              margin: '0 0 24px 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <Mail size={16} color="#3b82f6" />
              <span style={{
                color: 'var(--color-info-hover)',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {email}
              </span>
            </div>
          )}

          <div style={{
            background: 'var(--color-warning-bg-strong)',
            border: '1px solid #fed7aa',
            borderRadius: '8px',
            padding: '16px',
            margin: '0 0 24px 0',
            textAlign: 'left'
          }}>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--color-warning-fg)',
              margin: '0 0 8px 0'
            }}>
              📧 What to do next:
            </h3>
            <ul style={{
              fontSize: '14px',
              color: 'var(--color-warning-fg)',
              margin: 0,
              paddingLeft: '20px',
              lineHeight: '1.4'
            }}>
              <li>Check your email inbox (and spam folder)</li>
              <li>Click the verification link in the email</li>
              <li>Return here to login to your account</li>
            </ul>
          </div>

          {/* Resend Email Section */}
          <div style={{
            background: 'var(--color-bg-app)',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '20px',
            margin: '0 0 24px 0'
          }}>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--color-text-secondary)',
              margin: '0 0 12px 0'
            }}>
              Didn't receive the email?
            </h3>
            
            <Button
              onClick={handleResendEmail}
              disabled={resending}
              loading={resending}
              variant="secondary"
              icon={resending ? <RefreshCw size={16} /> : <Send size={16} />}
              style={{ marginBottom: '12px' }}
            >
              {resending ? 'Sending...' : 'Resend Verification Email'}
            </Button>
            
            {resendMessage && (
              <div style={{
                padding: '8px 12px',
                borderRadius: '4px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                backgroundColor: resendStatus === 'success' ? 'var(--color-success-bg)' : 'var(--color-danger-bg)',
                color: resendStatus === 'success' ? '#166534' : 'var(--color-danger-hover)',
                border: `1px solid ${resendStatus === 'success' ? 'var(--color-success-border)' : 'var(--color-danger-border)'}`
              }}>
                {resendStatus === 'success' ? (
                  <CheckCircle2 size={14} />
                ) : (
                  <AlertCircle size={14} />
                )}
                {resendMessage}
              </div>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <Button
              onClick={handleBackToLogin}
              variant="secondary"
              icon={<ArrowLeft size={16} />}
            >
              Back to Login
            </Button>
            <Button
              onClick={handleBackToRegister}
              variant="secondary"
            >
              Register Different Email
            </Button>
          </div>
        </Card.Content>
      </Card>
    </div>
  );
};

export default VerificationPending;